import Checkbox from 'src/components/Checkbox';
import FontAwesomeIcon from 'src/components/Icon';
import GroupedHeaderControls from 'src/pages/ServiceRequests/GroupedHeaderControls';
import { convertTime } from 'src/helpers/time';
import Label from 'src/components/Label';
import { sortByDate } from 'src/components/Table/sorting';
import moment from 'moment';
import { Link } from 'react-router-dom';
import { ICONS, PATHS, SuccessMessages } from 'src/common/constants';
import IcoMoon from 'src/components/IcoMoon';
import Status from 'src/components/Status';
import { displayStatusLabel } from 'src/utils/serviceRequestStatus/displayStatusLabel';
import ExpandableText from 'src/components/ExpandableText/ExpandableText';
import { Attachments } from 'src/pages/ServiceRequests/components/Attachments';
import { downloadDocument } from 'src/helpers/DownloadDoc';
import { ToastInfo } from 'src/helpers/validationHelper';
import { ITableColumn } from 'src/components/Table/types';
import {
  CreateTableColumnsParams,
  INTERNAL_ONLY_COLUMN_ACCESSORS,
} from 'src/pages/ServiceRequests/components/helpers';

const isValidDate = (date: any) => date !== null && moment(date).year() > 2020;

/* Per-page SR column factory. Mirrors createServiceRequestTableColumns minus
 * the 3 dashboard-only columns (Ticket #, Primary Homeowner, Issue Type) whose
 * accessors only exist on the SR dashboard's API response. Shared by Contact /
 * Property / Suite SR pages — light duplication is intentional, each table has
 * a single explicit source of truth instead of a flag-driven branch */
export const createPerPageServiceRequestTableColumns = ({
  allSelected,
  onSelectAll,
  expandedGroups,
  setExpandedGroups,
  selectedServiceRequests,
  onSelectGroup,
  onDeleteGroup,
  onSelectServiceRequest,
  onRemoveFromGroup,
  branding,
  hasTarionNumbers,
  onClickAttachment,
  navigateTo = PATHS.SERVICE_REQUESTS_LIST,
  navLabel,
  canSeeInternalColumns,
}: CreateTableColumnsParams): ITableColumn[] => {
  const baseColumns: ITableColumn[] = [
    {
      label: <Checkbox readOnly value={allSelected} onChange={onSelectAll} />,
      width: '8rem',
      sticky: true,
      accessor: 'selection',
      columnSelectionLabel: 'Select',
      columnSelectionDisabled: true,
      groupedHeaderCell: (data: any) => {
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <GroupedHeaderControls
              data={data}
              onExpandGroup={() =>
                setExpandedGroups([...expandedGroups, data.id])
              }
              onContractGroup={() =>
                setExpandedGroups(
                  expandedGroups.filter((id: any) => id !== data.id)
                )
              }
              isSelected={
                allSelected ||
                data.children?.every((c) =>
                  selectedServiceRequests.includes(c.id)
                )
              }
              onSelectGroup={() =>
                onSelectGroup(data.children?.map((c) => c.id))
              }
              onDeleteGroup={() => onDeleteGroup(data.id)}
            />
          </div>
        );
      },
      cell: (data: any) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          {data.isChild && (
            <div
              style={{ cursor: 'pointer' }}
              onClick={() => onRemoveFromGroup(data.id, data.groupId)}
            >
              <FontAwesomeIcon
                glyph="object-ungroup"
                weight="regular"
                size="big"
              />
            </div>
          )}
          <Checkbox
            checked={selectedServiceRequests.includes(data.id) || allSelected}
            name={data.id}
            value={selectedServiceRequests.includes(data.id) || allSelected}
            onChange={() => onSelectServiceRequest(data.id)}
            readOnly
          />
        </div>
      ),
    },
    {
      label: `${branding?.text?.suite} | SL#`,
      accessor: 'name',
      sortable: true,
      sticky: true,
      columnSelectionDisabled: true,
      groupedHeaderCell: (data) => {
        return (
          <>
            <span
              onClick={() => {
                setExpandedGroups(
                  expandedGroups.includes(data.id)
                    ? expandedGroups.filter((id: any) => id !== data.id)
                    : [...expandedGroups, data.id]
                );
              }}
              style={{
                cursor: 'pointer',
                fontWeight: 600,
              }}
            >
              {data.name}
            </span>
            {data.walkthroughPdfUrl && (
              <div
                style={{
                  cursor: 'pointer',
                  marginLeft: '1rem',
                  color: '#0a0a0a',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  whiteSpace: 'nowrap',
                }}
                title="Download Walkthrough PDF"
                onClick={(e) => {
                  e.stopPropagation();
                  ToastInfo(SuccessMessages.pdfDownloadNotification);
                  downloadDocument(data.walkthroughPdfUrl);
                }}
              >
                <FontAwesomeIcon glyph="download" weight="regular" />
              </div>
            )}
          </>
        );
      },
      minWidth: '12rem',
      width: '0.25fr',
      groupedHeaderCellSpan: hasTarionNumbers ? 10 : 9,
      xsGroupedHeaderCellSpan: hasTarionNumbers ? 10 : 9,
      cell: (data) => (
        <Link
          to={{
            pathname: PATHS.SERVICE_REQUESTS_VIEW.replace(':id', data.id),
            state: { navigateTo, navLabel },
          }}
        >
          {`${data.name}` + (data.stratalot ? ` | ${data.stratalot}` : '')}
        </Link>
      ),
    },
    {
      label: 'Description',
      accessor: 'description',
      textOverflow: true,
      sortable: true,
      xsHide: false,
      minWidth: '38rem',
      columnDefaultSelection: true,
      cell: (data: any) => {
        const description: string = data.description?.trim() ?? '';
        return <ExpandableText text={description} characterLimit={80} />;
      },
    },
    {
      label: 'Area',
      accessor: 'area',
      xsHide: false,
      width: '12rem',
      sortable: true,
      columnDefaultSelection: true,
      cell: (data: any) => (
        <ExpandableText text={data.area?.trim() ?? ''} characterLimit={24} />
      ),
    },
    {
      label: 'Asset',
      xsHide: false,
      accessor: 'item',
      columnDefaultSelection: true,
      width: '15rem',
      sortable: true,
      cell: (data: any) => (
        <ExpandableText text={data.item?.trim() ?? ''} characterLimit={30} />
      ),
    },
    {
      label: 'Type',
      xsHide: false,
      accessor: 'type',
      width: '11rem',
      sortable: true,
      columnDefaultSelection: true,
    },
    {
      label: 'Status',
      showOverflow: true,
      accessor: 'status',
      width: '15rem',
      xsHide: false,
      sortable: true,
      columnDefaultSelection: true,
      cell: (data: any) => (
        <Status
          icon={<IcoMoon glyph={ICONS[data.status]} />}
          label={displayStatusLabel(data.status, data.customLabel)}
        />
      ),
    },
    {
      label: 'Visibility',
      accessor: 'isExternallyVisible',
      sortable: true,
      xsHide: false,
      width: '12rem',
      columnDefaultSelection: true,
      cell: (data: any) =>
        data.isExternallyVisible === false ? 'Internal' : 'External',
    },
    {
      label: 'Submitted On',
      sortable: true,
      accessor: 'createdDate',
      sortingAlgorithm: sortByDate,
      cell: (data: any) => {
        const days: number = moment().diff(moment(data.createdDate), 'days');
        const color: 'teal' | 'yellow' | 'red' =
          days <= 30 ? 'teal' : days <= 60 ? 'yellow' : 'red';
        return (
          <Label color={color} label={convertTime(data.createdDate, 'date')} />
        );
      },
      columnDefaultSelection: true,
      width: '14rem',
    },
    {
      label: 'Assigned To',
      accessor: 'serviceProvider',
      width: '18rem',
      sortable: true,
      columnDefaultSelection: true,
    },
    {
      label: 'Scheduled',
      accessor: 'scheduledAt',
      sortable: true,
      xsHide: false,
      sortingAlgorithm: sortByDate,
      columnDefaultSelection: true,
      cell: (data: any) =>
        isValidDate(data.scheduledAt)
          ? convertTime(data.scheduledAt, 'date')
          : '',
      width: '10rem',
    },
  ];

  const tarionColumn = {
    label: 'Tarion #',
    accessor: 'tarionItemNumber',
    sortable: true,
    xsHide: false,
    style: { width: '8rem' },
    minWidth: '8rem',
    columnDefaultSelection: true,
    cell: (data: any) => data.tarionItemNumber || '',
  };

  const columns: ITableColumn[] = hasTarionNumbers
    ? [...baseColumns.slice(0, 6), tarionColumn, ...baseColumns.slice(6)]
    : baseColumns;

  // Internal-only columns are hidden from external parties (home owners, realtors, occupants).
  if (canSeeInternalColumns) return columns;

  return columns.filter(
    (column) =>
      !INTERNAL_ONLY_COLUMN_ACCESSORS.includes(column.accessor as string)
  );
};
